import {
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  styled,
  tooltipClasses,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import { useLoading } from "../../../context/LoadingContext";
import TableSkeleton from "../../operator/Components/TableSkeleton";
import { theme } from "../../../components/theme";
import {
  customDateConvert,
  findTimeDifference,
} from "../../../components/Common/Utils/Utils";

const cellStyle = {
  whiteSpace: "nowrap",
  borderRight: "1px solid #ddd",
  // textAlign: "center",
  fontSize: "13px",
  py: 0.45,
};

const cellLinkStyle = {
  ...cellStyle,
  color: "#00A398",
  textDecoration: "underline",
  cursor: "pointer",
};

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "transparent",
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: "0px 3px 6px #0000001F",
    zIndex: "1",
  },
}));

const DescTooltip = ({ children, data, placement = "right" }) => {
  return (
    <LightTooltip
      title={
        <div
          style={{
            backgroundColor: "#0077B6",
            color: "#FFF",
            margin: "-5px -8px",
            boxShadow: "none",
            borderRadius: "5px",
            fontSize: "13px",
            fontWeight: "600",
            padding: "8px 18px",
          }}
        >
          Percentage : {data || 0} %
        </div>
      }
      placement={placement}
    >
      {children}
    </LightTooltip>
  );
};

const ClickableCell = ({ id, name, prevData }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    localStorage.setItem("currentState", prevData);
    navigate(`/qualityInfo/${id}`, { state: { prevData } });
  };

  return (
    <span
      onClick={handleClick}
      style={{
        cursor: "pointer",
        color: "#0077B6",
        textDecoration: "underline",
      }}
    >
      {name}
    </span>
  );
};
function convertTime(input) {
  const parts = input.split(":");

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parseInt(parts[2], 10);

  const totalSeconds = hours * 3600 + minutes * 60 + seconds;

  const days = Math.floor(totalSeconds / (24 * 3600));
  const remainingHours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
  const remainingMinutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  return `${days}Days ${remainingHours}Hrs ${remainingMinutes}Min ${remainingSeconds}Sec`;
}

function ProjectsTableBody({ data }) {
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);
  const formatTimeDifference = (exitTime) => {
    const exitDate = new Date(exitTime);
    const diffInSeconds = Math.floor((currentTime - exitDate) / 1000);

    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };
  return (
    <>
      <TableBody>
        {data?.length === 0 ? (
          <TableRow hover sx={{ cursor: "pointer" }}>
            <TableCell scope="row" align="center" colSpan={20}>
              <div
                style={{
                  textAlign: "center",
                  color: "red",
                  fontSize: "20px",
                  width: "100%",
                }}
              >
                No data available
              </div>
            </TableCell>
          </TableRow>
        ) : (
          data?.map((row, rowIndex) => {
            const maxContribution = Math.max(
              ...(row?.factors?.map((factor) => factor.contribution) || [])
            );

            return (
              <TableRow key={rowIndex}>
                <TableCell sx={cellStyle}>
                  {customDateConvert(
                    row?.dryTunnelExitTime,
                    "MM/DD/YYYY HH:mm:ss"
                  )}
                </TableCell>
                <TableCell sx={cellStyle}>
                  {row?.partCode ? (
                    <ClickableCell
                      id={row.partSystemId}
                      name={row.partCode}
                      prevData={"By Parts"}
                    />
                  ) : (
                    <span style={{ visibility: "hidden" }}>Undefined</span>
                  )}
                </TableCell>
                <TableCell sx={cellStyle}>
                  <span style={{ display: "flex", alignItems: "center" }}>
                    {row?.final_status && (
                      <StopRoundedIcon
                        sx={{
                          color: theme.colors[row?.final_status?.toLowerCase()],
                          fontSize: "30px",
                        }}
                      />
                    )}
                    {row?.final_status || "NA"}
                  </span>
                </TableCell>
                <TableCell
                  sx={{
                    ...cellStyle,
                    color: row?.actualStatus === "Good" ? "#34A853" : "#EA4335",
                  }}
                >
                  {row?.actualStatus}
                </TableCell>
                <TableCell sx={cellStyle}>
                  {row?.productSystemId ? (
                    <ClickableCell
                      id={row.productSystemId}
                      name={row.productCode}
                      prevData={"By Products"}
                    />
                  ) : (
                    <span style={{ visibility: "hidden" }}>Undefined</span>
                  )}
                </TableCell>
                <TableCell sx={cellStyle}>
                  {/* {row?.paternosterTimer || "00:00"} */}
                  {row.paternosterTimer === "on"
                    ? convertTime(formatTimeDifference(row?.dryTunnelExitTime))
                    : findTimeDifference(
                        row.paternosterTimer,
                        row?.dryTunnelExitTime
                      )}
                </TableCell>
                {row?.factors?.map((factor, factorIndex) => (
                  <TableCell
                    key={factorIndex}
                    sx={{
                      ...cellStyle,
                      backgroundColor:
                        factor.contribution === maxContribution
                          ? "#FDECEA"
                          : "",
                    }}
                  >
                    <DescTooltip data={factor?.contribution} placement="top">
                      <Typography fontSize={12} fontWeight={600}>
                        {factor?.actualValue}
                      </Typography>
                    </DescTooltip>
                  </TableCell>
                ))}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </>
  );
}

export default ProjectsTableBody;
